/* ============================================================================
 * Ory Response Reader - Infrastructure Layer Utility
 * ============================================================================
 *
 * Provides type guards and data extraction for Ory API responses.
 * Infrastructure primitives only - no business logic or presentation concerns.
 *
 * Consolidates:
 * - Response type guards (proper TypeScript type narrowing)
 * - Response data extraction (AAL verification, redirect URL resolution)
 *
 * Domain services use these primitives to make business decisions.
 * Presentation layer uses these for navigation and UI state management.
 *
 * @see OryFlowReader - For reading data from flow UI structures
 */

import type {
  ContinueWith,
  ContinueWithRedirectBrowserTo,
  ContinueWithVerificationUi,
  ContinueWithSettingsUi,
  ContinueWithRecoveryUi,
  OAuth2RedirectTo,
} from '@ory/client';
import type { OryResponse, OryGetOutput } from '../model';

// ============================================================================
// Response Type Guards
// ============================================================================

/**
 * Checks if response contains a session.
 * Narrows to response with typed session.
 */
export const isOryResponseWithSession = <IdentityTraits extends object = object>(
  response: OryResponse
): response is OryResponse & { session: OryGetOutput<'session', IdentityTraits> } => {
  return typeof response === 'object' && response !== null && Object.prototype.hasOwnProperty.call(response, 'session');
};

/**
 * Checks if response contains continue_with array.
 * Narrows to responses with ContinueWith actions.
 */
export const isOryResponseWithContinueWith = (
  response: OryResponse
): response is OryResponse & { continue_with: ContinueWith[] } => {
  return typeof response === 'object' && response !== null && Object.prototype.hasOwnProperty.call(response, 'continue_with');
};

/**
 * Checks if response is OAuth2RedirectTo (OAuth consent response).
 * Properly narrows to OAuth2RedirectTo type with redirect_to property.
 */
export const isOAuth2RedirectTo = (
  response: OryResponse
): response is OAuth2RedirectTo => {
  return (
    typeof response === 'object' &&
    response !== null &&
    'redirect_to' in response &&
    typeof (response as { redirect_to: unknown }).redirect_to === 'string'
  );
};

// ============================================================================
// ContinueWith Action Type Guards (Private - used internally)
// ============================================================================

/*
 * Type guards for ContinueWith action types.
 * Provide proper TypeScript type narrowing for Ory's discriminated union.
 *
 * Ory's ContinueWith structure:
 * type ContinueWith =
 *   | ({ action: 'redirect_browser_to' } & ContinueWithRedirectBrowserTo)
 *   | ({ action: 'set_ory_session_token' } & ContinueWithSetOrySessionToken)
 *   | ({ action: 'show_verification_ui' } & ContinueWithVerificationUi)
 *   | ...etc
 *
 * The runtime check on .action property properly narrows the union type.
 */
function isContinueWithRedirect(action: ContinueWith): action is { action: 'redirect_browser_to' } & ContinueWithRedirectBrowserTo {
  return action.action === 'redirect_browser_to';
}

function isContinueWithVerification(action: ContinueWith): action is { action: 'show_verification_ui' } & ContinueWithVerificationUi {
  return action.action === 'show_verification_ui';
}

function isContinueWithSettings(action: ContinueWith): action is { action: 'show_settings_ui' } & ContinueWithSettingsUi {
  return action.action === 'show_settings_ui';
}

function isContinueWithRecovery(action: ContinueWith): action is { action: 'show_recovery_ui' } & ContinueWithRecoveryUi {
  return action.action === 'show_recovery_ui';
}

// ============================================================================
// Response Data Extraction
// ============================================================================

/**
 * Infrastructure utility for reading data from Ory API responses.
 *
 * Provides primitives for extracting typed data from Ory responses.
 * Does not make business or presentation decisions - callers use these
 * primitives to implement their own logic.
 *
 * @example
 * ```typescript
 * // Infrastructure layer: Extract data
 * const response = await oryService.executeFlow('login', flow, data);
 * const redirectUrl = OryResponseReader.redirectUrl(response);
 * const hasAuth = OryResponseReader.hasMinimumAAL(response, 'aal1');
 *
 * // Domain/Presentation layer: Make decisions
 * if (hasAuth && redirectUrl) {
 *   // Business logic decides what to do with the data
 * }
 * ```
 */
export class OryResponseReader {

  /**
   * Checks if response has minimum required AAL (Authentication Assurance Level).
   *
   * @remarks
   * Infrastructure primitive for verifying authentication strength.
   * Returns true if session exists with minimum AAL or higher.
   * AAL2 satisfies both AAL1 and AAL2 requirements.
   *
   * AAL levels:
   * - `'aal1'` - Single-factor authentication (password, code, etc.)
   * - `'aal2'` - Two-factor authentication (password + TOTP/WebAuthn)
   *
   * Uses generics for proper type narrowing - no casting required.
   *
   * @see https://www.ory.com/docs/kratos/mfa/overview
   * @see https://www.ory.com/docs/kratos/mfa/step-up-authentication
   *
   * @param response - Ory API response to check
   * @param minimumAal - Minimum required AAL level
   *
   * @example
   * ```typescript
   * // Check single-factor authentication
   * if (OryResponseReader.hasMinimumAAL(response, 'aal1')) {
   *   // Session exists with at least password authentication
   * }
   *
   * // Require two-factor authentication
   * if (!OryResponseReader.hasMinimumAAL(response, 'aal2')) {
   *   // Session lacks MFA, need to prompt for second factor
   * }
   * ```
   */
  static hasMinimumAAL<IdentityTraits extends object = object>(
    response: OryResponse,
    minimumAal: 'aal1' | 'aal2'
  ): boolean {
    // Type guard narrows to OryResponse & { session: OryGetOutput<'session', IdentityTraits> }
    // No casting needed!
    if (!isOryResponseWithSession<IdentityTraits>(response)) {
      return false;
    }

    const aal = response.session.authenticator_assurance_level;

    // Ory's AuthenticatorAssuranceLevel enum includes sentinel value
    // Only accept valid AAL values
    if (aal !== 'aal1' && aal !== 'aal2') {
      return false;
    }

    // aal2 satisfies both aal1 and aal2 requirements
    if (minimumAal === 'aal1') {
      return aal === 'aal1' || aal === 'aal2';
    }

    return aal === 'aal2';
  }

  /**
   * Extracts redirect URL from Ory response.
   *
   * @remarks
   * Single source of truth for redirect priority in Ory responses.
   * Implements Ory's semantic priority order:
   *
   * 1. **Flow UI actions** (show_verification_ui, show_settings_ui, show_recovery_ui)
   *    - Explicit navigation instructions from Ory (e.g., "verify your email")
   *    - These indicate the next step in the self-service flow
   *
   * 2. **Browser redirects** (redirect_browser_to)
   *    - Can be external OAuth providers, consent callbacks, or internal endpoints
   *    - May point to internal API endpoints like `/session`
   *
   * Returns undefined when no redirect action exists, allowing caller to
   * implement fallback logic (return_to parameter, default destination, etc.).
   *
   * Caller responsibility: Distinguish external URLs (full page redirect)
   * from internal routes (SPA navigation).
   *
   * @see https://www.ory.com/docs/kratos/self-service/flows/user-login
   * @see https://www.ory.com/docs/kratos/concepts/browser-redirect-flow-completion
   *
   * @example
   * ```typescript
   * // Extract redirect URL (infrastructure primitive)
   * const response = await oryService.executeFlow('registration', flow, data);
   * const redirectUrl = OryResponseReader.redirectUrl(response);
   *
   * // Example result priorities:
   * // 1. show_verification_ui → "http://localhost:5100/verification?flow=123"
   * // 2. redirect_browser_to → "/session" or "https://github.com/login/oauth"
   * // 3. No action → undefined
   *
   * // Caller implements navigation logic
   * if (redirectUrl) {
   *   // Domain/presentation layer decides how to handle
   * }
   * ```
   */
  static redirectUrl(response: OryResponse): string | undefined {
    // Handle OAuth2RedirectTo (consent responses) - proper type guard
    if (isOAuth2RedirectTo(response)) {
      return response.redirect_to;
    }

    // Handle regular flow responses with continue_with array
    if (!isOryResponseWithContinueWith(response)) {
      return undefined;
    }

    if (!response.continue_with || response.continue_with.length === 0) {
      return undefined;
    }

    // Single-pass priority resolution
    let browserRedirect: string | undefined;

    for (const action of response.continue_with) {
      // Priority 1: Flow UI actions (explicit user navigation)
      // These tell the user where to go next in the self-service flow
      if (isContinueWithVerification(action) && action.flow.url) {
        return action.flow.url;
      }
      if (isContinueWithSettings(action) && action.flow.url) {
        return action.flow.url;
      }
      if (isContinueWithRecovery(action) && action.flow.url) {
        return action.flow.url;
      }

      // Priority 2: Browser redirects (store first occurrence)
      // Can be OAuth providers, consent callbacks, or API endpoints like /session
      if (isContinueWithRedirect(action) && !browserRedirect) {
        browserRedirect = action.redirect_browser_to;
      }
    }

    // Return browser redirect if no flow UI action found
    return browserRedirect;
  }
}
